import { useSEO } from "@/hooks/useSEO";
import { SITE } from "@/config/siteConfig";

const CONTENT = {
  "privacy-policy": {
    title: "Privacy Policy",
    seoTitle: "Privacy Policy | Book Kashmir",
    sections: [
      { h: "Information We Collect", p: "When you submit an enquiry on www.bookkashmir.in, we collect your name, phone number, email address (if provided), travel preferences and the page or campaign you arrived from. We collect only what is needed to respond to your enquiry and plan your holiday." },
      { h: "How We Use Your Information", p: "Your details are used solely to contact you about your Kashmir holiday enquiry, prepare quotes and itineraries, and — with your consent — share relevant travel updates. We do not sell or rent your personal information to third parties." },
      { h: "Data Sharing", p: "We may share necessary details (such as traveller names) with hotels and transport providers only when you confirm a booking. Analytics and advertising partners may receive anonymised usage data where tracking is enabled." },
      { h: "Data Security & Retention", p: "Enquiry data is stored securely and retained only as long as needed to service your enquiry and meet legal obligations. You may request access, correction or deletion of your data by contacting us." },
      { h: "Contact", p: `For any privacy-related questions, contact us at ${SITE.email} or call ${SITE.phoneDisplay}.` },
    ],
  },
  "terms-and-conditions": {
    title: "Terms & Conditions",
    seoTitle: "Terms & Conditions | Book Kashmir",
    sections: [
      { h: "Bookings & Quotes", p: "All quotations are personalised and valid for a limited period, subject to hotel and transport availability. A booking is confirmed only after receipt of the advance payment communicated in your quote." },
      { h: "Package Inclusions", p: "Inclusions vary by selected package and hotel category. Your confirmed itinerary and quote list exactly what is included; anything not listed is excluded." },
      { h: "Payments", p: "Payment schedules are shared with your quote. Balance payment terms are confirmed at the time of booking. We do not store card or banking details on this website." },
      { h: "Travel Conditions", p: "Mountain travel is subject to weather and road conditions. Sightseeing may be adjusted for safety or local restrictions; such changes do not constitute a breach of these terms. Gondola tickets, pony rides and local union vehicles are payable directly unless specifically included." },
      { h: "Traveller Responsibilities", p: "Travellers must carry valid government photo ID and follow local regulations and safety guidance during the trip." },
      { h: "Contact", p: `Questions about these terms? Email ${SITE.email} or call ${SITE.phoneDisplay}.` },
    ],
  },
  "cancellation-refund-policy": {
    title: "Cancellation & Refund Policy",
    seoTitle: "Cancellation & Refund Policy | Book Kashmir",
    sections: [
      { h: "Cancellation by You", p: "If you need to cancel your trip, inform us in writing (email or WhatsApp) as early as possible. Cancellation charges depend on how close to the travel date the cancellation is made and on the policies of the hotels and providers booked for your trip. The exact schedule applicable to your booking is shared with your quote." },
      { h: "Refunds", p: "Eligible refunds are processed back to the original payment method after deducting applicable cancellation charges and any non-refundable amounts paid to hotels or suppliers. Processing typically takes 7-14 working days after confirmation." },
      { h: "Amendments", p: "Date or itinerary changes are accommodated wherever possible and may attract revision charges depending on hotel and transport availability." },
      { h: "Force Majeure", p: "In case of weather events, road closures or circumstances beyond our control, we will work with you to reschedule or adjust your trip. Refunds in such cases follow supplier policies." },
      { h: "Contact", p: `For cancellation or refund requests, contact us at ${SITE.email} or WhatsApp ${SITE.phoneDisplay} with your booking details.` },
    ],
  },
};

const LegalPage = ({ pageKey }) => {
  const content = CONTENT[pageKey];
  useSEO({
    title: content.seoTitle,
    description: `${content.title} for Book Kashmir — www.bookkashmir.in`,
    path: `/${pageKey}`,
  });

  return (
    <main className="py-16 sm:py-24" data-testid={`legal-page-${pageKey}`}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-mono uppercase tracking-[0.2em] font-semibold text-[#C28519] mb-3">Book Kashmir</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-[#0F2F24]">{content.title}</h1>
        <p className="mt-2 text-xs text-slate-500">Last updated: July 2026</p>
        <div className="mt-10 space-y-8">
          {content.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-serif text-xl font-semibold text-[#0F2F24] mb-2">{s.h}</h2>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
};

export default LegalPage;
